import { WIN, isNoisy, scoreRootMoves, type BotThought } from './bot';
import { actionKey, actionToSan, applyAction, legalActions, viewFor } from './engine';
import { sqName } from './geometry';
import type { Action, Color, GameState } from './types';

// Move feedback: compare the played move with the best move found by a short search, judged by
// how much it changes the mover's winning chances and by what happens to every piece on the board
// once the captures that follow are played out.

export type MoveQuality =
  | 'brilliant' | 'great' | 'best' | 'excellent' | 'good' | 'inaccuracy' | 'mistake' | 'miss' | 'blunder' | 'forced';

export const QUALITY_INFO: Record<MoveQuality, { label: string; symbol: string; color: string }> = {
  brilliant: { label: 'Brilliant', symbol: '!!', color: '#1d9e96' },
  great: { label: 'Great move', symbol: '!', color: '#4a7fc0' },
  best: { label: 'Best move', symbol: '★', color: '#5c9a3e' },
  excellent: { label: 'Excellent', symbol: '✓', color: '#7fae54' },
  good: { label: 'Good', symbol: '✓', color: '#8b9a74' },
  inaccuracy: { label: 'Inaccuracy', symbol: '?!', color: '#d4a12e' },
  mistake: { label: 'Mistake', symbol: '?', color: '#e0783a' },
  miss: { label: 'Miss', symbol: '✗', color: '#d0603a' },
  blunder: { label: 'Blunder', symbol: '??', color: '#c63f2d' },
  forced: { label: 'Forced', symbol: '→', color: '#9a958c' },
};

export interface MoveReview {
  quality: MoveQuality;
  /** Points (1 pawn = 100) given up compared with the best move. */
  loss: number;
  best: Action;
  /** Material was given up on purpose (any piece, not only the moved one). */
  sacrifice: boolean;
  /** The mover's winning chances in percent with the best move and with the played move. */
  winBest: number;
  winPlayed: number;
  /** One sentence on why, e.g. "Leaves the rook on a1 hanging: Bxa1. Best was Qd2." */
  note: string;
}

// Mates are worth "a lot", but should not dwarf every other difference.
const clamp = (v: number) => Math.max(-3000, Math.min(3000, v));

/** Winning chances in percent for an advantage in points (the usual logistic curve). */
export function winChance(cp: number): number {
  return 50 + 50 * (2 / (1 + Math.exp(-0.00368208 * clamp(cp))) - 1);
}

/**
 * Quality from the drop in winning chances (percentage points) compared with the best move.
 * `top` means the move was the best one (or just as good).
 */
export function qualityFor(winLoss: number, opts: { top?: boolean; sacrifice?: boolean; onlyMove?: boolean; fine?: boolean } = {}): MoveQuality {
  const top = opts.top ?? winLoss <= 1;
  const fine = opts.fine ?? true;
  if (top) {
    if (opts.sacrifice && fine) return 'brilliant';
    if (opts.onlyMove && fine) return 'great';
    return 'best';
  }
  if (winLoss <= 3) return 'excellent';
  if (winLoss <= 7) return 'good';
  if (winLoss <= 12) return 'inaccuracy';
  if (winLoss <= 22) return 'mistake';
  return 'blunder';
}

const pieceValue = (s: GameState, sq: number) => {
  const p = s.board[sq];
  const d = p && s.rs.pieces[p.type];
  return d ? (d.royal ? 0 : d.value) : 0;
};

/** Material of `me` minus the opponent's, in points; a finished game counts as a huge swing. */
function material(s: GameState, me: Color): number {
  if (s.result) return !s.result.winner ? 0 : s.result.winner === me ? 10_000 : -10_000;
  let m = 0;
  for (let sq = 0; sq < s.board.length; sq++) {
    const p = s.board[sq];
    if (p) m += (p.color === me ? 1 : -1) * pieceValue(s, sq) * 100;
  }
  return m;
}

interface SwingCtx { nodes: number; max: number }

/**
 * Material for `me` once both sides have made every capture worth making (either side may stop).
 * This sees every piece that hangs after a move, not only the moved one.
 */
function settle(s: GameState, me: Color, qd: number, alpha: number, beta: number, ctx: SwingCtx, pv: Action[]): number {
  pv.length = 0;
  const stand = material(s, me);
  if (s.result || qd <= 0 || ++ctx.nodes > ctx.max) return stand;
  const maximizing = s.turn === me;
  if (maximizing ? stand >= beta : stand <= alpha) return stand;
  if (maximizing) alpha = Math.max(alpha, stand); else beta = Math.min(beta, stand);
  let best = stand;
  const line: Action[] = [];
  const caps = legalActions(s).filter((a) => isNoisy(s, a))
    .sort((a, b) => (a.t === 'move' && b.t === 'move' ? pieceValue(s, b.to) - pieceValue(s, a.to) : 0));
  for (const a of caps) {
    const v = settle(applyAction(s, a).state, me, qd - 1, alpha, beta, ctx, line);
    if (maximizing ? v > best : v < best) { best = v; pv.length = 0; pv.push(a, ...line); }
    if (maximizing) alpha = Math.max(alpha, v); else beta = Math.min(beta, v);
    if (beta <= alpha) break;
  }
  return best;
}

interface Settled { drop: number; line: Action[]; state: GameState }

/** How much material `me` loses (negative: wins) by playing `a` from `s` once the captures settle. */
function settleAfter(s: GameState, a: Action, me: Color, max = 3000): Settled {
  const state = applyAction(s, a).state;
  const line: Action[] = [];
  const v = settle(state, me, 6, -Infinity, Infinity, { nodes: 0, max }, line);
  return { drop: material(s, me) - v, line, state };
}

interface Capture { by: Color; name: string; sq: number; value: number }

/** Replay `line` from `s` and list the pieces it captures. */
function capturesOf(s: GameState, line: Action[]): Capture[] {
  const out: Capture[] = [];
  let cur = s;
  for (const a of line) {
    if (a.t === 'move') {
      const sq = a.epCapture ?? a.to;
      const victim = cur.board[sq];
      if (victim && victim.color !== cur.turn) {
        out.push({ by: cur.turn, name: (cur.rs.pieces[victim.type]?.name ?? 'piece').toLowerCase(), sq, value: pieceValue(cur, sq) });
      }
    }
    cur = applyAction(cur, a).state;
  }
  return out;
}

const mostValuable = (caps: Capture[], by: Color) =>
  caps.filter((c) => c.by === by).sort((a, b) => b.value - a.value)[0];

/** SAN of the first `n` actions of `line`, played from `s`. */
function lineSan(s: GameState, line: Action[], n = 1): string {
  const out: string[] = [];
  let cur = s;
  for (const a of line.slice(0, n)) {
    const next = applyAction(cur, a).state;
    out.push(actionToSan(cur, a, next, cur.turn));
    cur = next;
  }
  return out.join(' ');
}

/** Moves of the winning side until mate for a mate score, else null. */
function mateIn(score: number, s: GameState): number | null {
  if (Math.abs(score) < WIN / 2) return null;
  const plies = WIN - Math.abs(score) - s.ply;
  return Math.max(1, Math.ceil(plies / (2 * Math.max(1, s.rs.rules.movesPerTurn))));
}

/**
 * Rate `played` in the position `before` from the mover's point of view (only what the mover
 * could see is used). Returns null if the move cannot be matched (e.g. hidden information).
 */
export function reviewMove(before: GameState, played: Action, opts: { depth?: number; timeMs?: number } = {}): MoveReview | null {
  if (before.result) return null;
  const me = before.turn;
  const view = viewFor(before, me);
  const legal = legalActions(view);
  const key = actionKey(played);
  if (!legal.some((l) => actionKey(l) === key)) return null;
  if (legal.length === 1) {
    return { quality: 'forced', loss: 0, best: legal[0], sacrifice: false, winBest: 50, winPlayed: 50, note: 'The only legal move.' };
  }

  const timeMs = opts.timeMs ?? 900;
  const { scores } = scoreRootMoves(view, opts.depth ?? 3, timeMs, 0, Math.random, 6);
  if (!scores.length) return null;
  const best = scores[0];
  const mine = scores.find((x) => actionKey(x.action) === key);
  if (!mine) return null;
  const isBest = actionKey(best.action) === key;

  const after = applyAction(view, played).state;
  const won = after.result?.winner === me;
  const cpBest = clamp(best.score);
  const cpMine = won ? cpBest : clamp(mine.score);
  const loss = Math.max(0, cpBest - cpMine);
  const winBest = winChance(cpBest);
  const winPlayed = winChance(cpMine);
  const top = won || isBest || loss <= 10;

  // Material once the dust settles, for the played move and every alternative.
  const mineS = settleAfter(view, played, me);
  const bestS = isBest ? mineS : settleAfter(view, best.action, me);
  let minDrop = Math.min(mineS.drop, bestS.drop);
  const until = Date.now() + Math.min(400, timeMs / 2);
  for (const o of scores) {
    if (Date.now() > until) break;
    const k = actionKey(o.action);
    if (k === key || k === actionKey(best.action)) continue;
    minDrop = Math.min(minDrop, settleAfter(view, o.action, me, 600).drop);
  }
  // A sacrifice gives up at least two points more than the mover had to, anywhere on the board.
  const sacrifice = !won && mineS.drop >= 200 && mineS.drop - Math.max(0, minDrop) >= 200;

  // "Great" is for finding a quiet only move, not for simply taking what is hanging.
  const second = scores.find((x) => actionKey(x.action) !== actionKey(best.action));
  const onlyMove = isBest && !!second && mineS.drop > -150 && winBest - winChance(clamp(second.score)) >= 20;

  let quality = qualityFor(winBest - winPlayed, { top, sacrifice, onlyMove, fine: winPlayed >= 45 });
  // A miss: nothing was lost, but a clear win of material (or mate) was left on the board.
  const prize = mostValuable(capturesOf(view, [best.action, ...bestS.line]), me);
  const bestWins = (bestS.drop <= -200 && !!prize && prize.value >= 2) || (mateIn(best.score, view) != null && best.score > 0);
  if ((quality === 'mistake' || quality === 'blunder') && bestWins && mineS.drop <= 50 && mine.score > -WIN / 2) quality = 'miss';

  const note = explain({ view, after, me, quality, played, best, mine, mineS, bestS, minDrop, won });
  return { quality, loss, best: best.action, sacrifice, winBest, winPlayed, note };
}

function explain(c: {
  view: GameState; after: GameState; me: Color; quality: MoveQuality; played: Action;
  best: BotThought; mine: BotThought; mineS: Settled; bestS: Settled; minDrop: number; won: boolean;
}): string {
  const { view, after, me, quality, best, mine, mineS, bestS } = c;
  const them: Color = me === 'w' ? 'b' : 'w';
  const sq = (n: number) => sqName(n, view.w);
  const bestSan = lineSan(view, [best.action]);
  const lost = mostValuable(capturesOf(after, mineS.line), them);
  const gained = mostValuable(capturesOf(view, [c.played, ...mineS.line]), me);
  const mateFor = mateIn(mine.score, view);
  const reply = mine.line?.length ? lineSan(after, mine.line) : '';

  switch (quality) {
    case 'brilliant':
      return `Gives up the ${lost?.name ?? 'material'}${mateFor && mine.score > 0 ? `: mate in ${mateFor} follows.` : ' and gets more back.'}`;
    case 'great':
      return winChance(clamp(mine.score)) >= 60 ? 'The only move that keeps the advantage.' : 'The only move that holds the position.';
    case 'best':
      if (c.won) return 'Ends the game.';
      if (mateFor && mine.score > 0) return `Mate in ${mateFor}.`;
      if (gained && mineS.drop <= -100) return `Wins the ${gained.name}.`;
      return 'The best move.';
    case 'excellent':
      return `Nearly as good as the best move, ${bestSan}.`;
    case 'good':
      return `Fine, but ${bestSan} was stronger.`;
    case 'miss': {
      const bm = mateIn(best.score, view);
      const prize = mostValuable(capturesOf(view, [best.action, ...bestS.line]), me);
      return bm ? `Misses ${bestSan}, mate in ${bm}.` : `Misses ${bestSan}, which wins the ${prize?.name ?? 'material'}.`;    }
    default: {
      let why: string;
      if (mateFor && mine.score < 0) {
        why = mateFor === 1 && reply ? `Allows ${reply}.` : `Allows mate in ${mateFor}${reply ? `, starting with ${reply}` : ''}.`;
      }
      else if (lost && mineS.drop >= 100 && mineS.drop > Math.max(0, c.minDrop)) {
        why = `Leaves the ${lost.name} on ${sq(lost.sq)} hanging${mineS.line.length ? `: ${lineSan(after, mineS.line)}` : ''}.`;
      } else if (reply) why = `Allows ${reply}.`;
      else why = 'Weakens the position.';
      return `${why} Best was ${bestSan}.`;
    }
  }
}
